import React, { useEffect, useState } from "react";
import { Avatar } from "@/components/Avatar";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Send, Navigation, MapPin } from "lucide-react";
import { formatDistance } from "@/lib/geo";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const RADIUS_STEPS = [2, 5, 20, 100];

/**
 * Shows users within `radius` km, with an Invite button that creates a fresh
 * mini room and copies /invite/<code> to the clipboard.
 */
export default function NearbySheet({ open, onClose, onOpenProfile }) {
  const nav = useNavigate();
  const [radius, setRadius] = useState(2);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (r) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/nearby/users?radius=${r}`);
      setList(data);
    } catch (e) {
      setError(formatError(e));
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load(radius);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, radius]);

  const invite = async (u) => {
    try {
      const { data } = await api.post("/rooms/mini", {
        name: `with @${u.username}`,
        description: `Private room for you and @${u.username}.`,
        topic: "Nearby",
      });
      const url = `${window.location.origin}/invite/${data.invite_code}`;
      try { await navigator.clipboard.writeText(url); } catch {}
      toast.success("Invite link copied — send it to them");
      onClose?.();
      nav(`/room/${data.id}`);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0b0b0e] border-white/10 text-white max-w-md" data-testid="nearby-sheet">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Navigation className="w-4 h-4 text-fuchsia-400" /> Who's near you?
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Sorted by distance. Tap Invite to create a 4-seat mini room and copy the invite link.
          </DialogDescription>
        </DialogHeader>

        {/* Radius picker */}
        <div className="flex items-center gap-2 flex-wrap">
          {RADIUS_STEPS.map((r) => (
            <button
              key={r}
              onClick={() => setRadius(r)}
              data-testid={`radius-${r}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                radius === r
                  ? "bg-[#e81cff] border-[#e81cff] text-white"
                  : "border-white/10 hover:bg-white/5 text-zinc-300"
              }`}
            >
              {r < 100 ? `${r} km` : `${r}+ km`}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="max-h-[50vh] overflow-y-auto space-y-2 mt-2">
          {loading && <div className="text-sm text-zinc-500">Scanning the map…</div>}
          {!loading && error && (
            <div className="text-sm text-rose-300">{error}</div>
          )}
          {!loading && !error && list.length === 0 && (
            <div className="text-sm text-zinc-500 border border-dashed border-white/10 rounded-xl p-6 text-center">
              No one within {radius} km shared their location.<br />
              <span className="text-zinc-600 text-xs">Try a bigger radius above.</span>
            </div>
          )}
          {list.map((u) => (
            <div
              key={u.id}
              className="flex items-center gap-3 rounded-xl border border-white/5 bg-[#0c0c0f] p-3"
              data-testid={`nearby-user-row-${u.id}`}
            >
              <button onClick={() => onOpenProfile?.(u)} className="shrink-0 rounded-full hover:ring-2 hover:ring-fuchsia-500/60 transition">
                <Avatar user={u} size={44} />
              </button>
              <div className="flex-1 min-w-0">
                <button onClick={() => onOpenProfile?.(u)} className="font-semibold text-left truncate hover:text-white transition">
                  {u.username}
                </button>
                <div className="flex items-center gap-2 text-xs text-zinc-400 mt-0.5">
                  <span className="inline-flex items-center gap-1 text-fuchsia-300">
                    <Navigation className="w-3 h-3" /> {formatDistance(u.distance_km)}
                  </span>
                  {u.location && (
                    <span className="inline-flex items-center gap-1 text-zinc-500 truncate">
                      <MapPin className="w-3 h-3" /> {u.location}
                    </span>
                  )}
                  {u.pronouns && <span>· {u.pronouns}</span>}
                </div>
              </div>
              <button
                onClick={() => invite(u)}
                data-testid={`nearby-invite-${u.id}`}
                className="text-xs px-3 py-1.5 rounded-full bg-[#e81cff] hover:bg-[#ff4dff] text-white font-semibold inline-flex items-center gap-1.5 transition"
              >
                <Send className="w-3 h-3" /> Invite
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
